import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ArrowRight, ExternalLink, Quote, ChevronLeft, ChevronRight, X, Eye, Tv } from "lucide-react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helper: Instagram embed URL ────────────────────────
function toEmbedUrl(url) {
  if (!url) return "";
  if (url.includes("/embed")) return url;
  if (url.includes("instagram.com/reel/")) {
    const id = url.split("/reel/")[1]?.split("/")[0]?.split("?")[0];
    return `https://www.instagram.com/reel/${id}/embed/`;
  }
  if (url.includes("youtube.com/shorts/")) {
    const id = url.split("shorts/")[0]?.split("?")[0] || url.split("shorts/")[1]?.split("?")[0];
    return `https://www.youtube.com/embed/${url.split("shorts/")[1]?.split("?")[0]}`;
  }
  if (url.includes("youtu.be/")) {
    const id = url.split("youtu.be/")[1]?.split("?")[0];
    return `https://www.youtube.com/embed/${id}`;
  }
  return url;
}

// ── Helper: Extract YouTube thumbnail from any URL form ─
function extractYouTubeThumb(url) {
  if (!url) return "";
  let id = "";
  if (url.includes("youtube.com/embed/")) id = url.split("/embed/")[1]?.split(/[?&/]/)[0];
  else if (url.includes("youtube.com/shorts/")) id = url.split("shorts/")[1]?.split(/[?&/]/)[0];
  else if (url.includes("youtu.be/")) id = url.split("youtu.be/")[1]?.split(/[?&/]/)[0];
  else if (url.includes("youtube.com/watch")) {
    const m = url.match(/[?&]v=([^&]+)/);
    if (m) id = m[1];
  }
  return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : "";
}

// ── Helper: Auto-thumbnail fallback ─────────────────────
function getThumb(item) {
  if (item?.thumbnail) return item.thumbnail;
  return extractYouTubeThumb(item?.embedUrl || item?.videoUrl);
}

// ── Hook: forward vertical wheel on horizontal scroller to page
function useWheelPassthrough(ref, deps = []) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e) => {
      // If user is scrolling primarily vertically, pass it to the page
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        window.scrollBy({ top: e.deltaY });
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// ── Video Modal (supports multiple videos per project) ─
function VideoModal({ video, onClose, isVertical }) {
  const [idx, setIdx] = useState(0);
  // Build the playlist: primary embedUrl + any extras from `videos` (newline-separated)
  const playlist = (() => {
    if (!video) return [];
    const main = video.embedUrl || video.videoUrl;
    const extras = (video.videos || "")
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    return [main, ...extras].filter(Boolean);
  })();

  useEffect(() => { setIdx(0); }, [video?.id]);

  if (!video) return null;
  const current = playlist[idx] || video.embedUrl || video.videoUrl;
  const embed = toEmbedUrl(current);
  const hasMany = playlist.length > 1;
  const fullDesc = video.fullDescription || video.description;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 25 }}
        className={`${isVertical ? "w-full max-w-sm" : "w-full max-w-5xl"} rounded-2xl overflow-hidden border border-white/10 shadow-2xl cursor-default my-auto`}
        onClick={(e) => e.stopPropagation()}>
        <div className={`relative ${isVertical ? "aspect-[9/16]" : "aspect-video"} bg-black`}>
          <iframe key={idx} src={embed.includes("?") ? embed : `${embed}${embed.includes("youtube") ? "?autoplay=1&rel=0" : ""}`}
            title={video.title} className="w-full h-full border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen scrolling="no" />
          {hasMany && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + playlist.length) % playlist.length); }}
                aria-label="Previous video"
                data-testid="modal-video-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-[#E63946] hover:border-[#E63946] transition-all flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % playlist.length); }}
                aria-label="Next video"
                data-testid="modal-video-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-[#E63946] hover:border-[#E63946] transition-all flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur rounded-full border border-white/10">
                {playlist.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                    aria-label={`Go to video ${i + 1}`}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-[#E63946] w-4" : "bg-white/40 hover:bg-white"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        {(video.title || fullDesc) && (
          <div className="p-5 md:p-6 bg-[#0A0A0C]">
            {video.category && <span className="text-xs text-[#E63946] font-mono uppercase tracking-[0.2em] font-bold">{video.category}</span>}
            {video.title && <h3 className="mt-2 font-heading text-lg md:text-xl font-bold text-white">{video.title}</h3>}
            {fullDesc && <p className="mt-3 text-sm text-zinc-300/90 leading-relaxed">{fullDesc}</p>}
            {hasMany && <p className="mt-3 font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Video {idx + 1} of {playlist.length}</p>}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Count-up helper (eased) ────────────────────────────
function CountUp({ end, suffix = "", duration = 1800, delay = 0, trigger = true }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let raf;
    const start = performance.now() + delay;
    const tick = (now) => {
      if (now < start) { raf = requestAnimationFrame(tick); return; }
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setVal(Math.round(end * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, delay, trigger]);
  return <>{val}{suffix}</>;
}

// ── Hero Impact Stats (2x2 premium grid) ───────────────
const HERO_STATS = [
  { value: 9, suffix: "+", label: "Years Experience" },
  { value: 500, suffix: "K+", label: "Followers" },
  { text: "Millions+", label: "Views" },
  { text: "Global", label: "Brand Collabs" },
];

// ── 1. HERO ────────────────────────────────────────────
function PortfolioHero({ hero }) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  return (
    <section className="relative min-h-[90vh] md:min-h-screen flex items-center justify-center overflow-hidden py-20 md:py-0" data-testid="portfolio-hero">
      {/* Background video / fallback */}
      <div className="absolute inset-0">
        {hero.videoUrl && (
          <iframe src={hero.videoUrl} className={`w-full h-full border-0 pointer-events-none transition-opacity duration-1000 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
            style={{ transform: "scale(1.2)" }} allow="autoplay; fullscreen" title="Portfolio Reel"
            onLoad={() => setVideoLoaded(true)} />
        )}
        {/* Cinematic fallback gradient */}
        <div className="absolute inset-0 bg-[#050505]" style={{ background: "radial-gradient(ellipse at center, #0F0F11 0%, #050505 70%)" }} />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#050505]" />
      </div>

      <div className="relative z-10 text-center max-w-5xl mx-auto px-6">
        <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="font-mono text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#E63946]">George Emad</motion.span>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
          className="mt-4 font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white">PORTFOLIO</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="mt-6 text-sm md:text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">{hero.subtitle}</motion.p>

        {/* Impact stats — single-row premium animated grid */}
        <div className="mt-14 grid grid-cols-4 gap-px max-w-4xl mx-auto bg-white/[0.06] rounded-2xl overflow-hidden border border-white/10 backdrop-blur-sm" data-testid="hero-impact-stats">
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 1 + i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-[#050505] px-2 py-5 sm:px-4 sm:py-7 md:px-6 md:py-9 group hover:bg-white/[0.02] transition-colors duration-500"
              data-testid={`hero-stat-${i}`}
            >
              {/* Subtle accent glow on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                   style={{ background: "radial-gradient(circle at 50% 50%, rgba(230,57,70,0.08) 0%, transparent 70%)" }} />
              <div className="relative">
                <div className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tighter leading-none whitespace-nowrap">
                  {s.value !== undefined ? (
                    <CountUp end={s.value} suffix={s.suffix} delay={1000 + i * 150} />
                  ) : (
                    <span className="bg-gradient-to-b from-white to-zinc-400 bg-clip-text text-transparent">{s.text}</span>
                  )}
                </div>
                <div className="mt-2 md:mt-3 font-mono text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold whitespace-nowrap">
                  {s.label}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── 2. FEATURED WORK ───────────────────────────────────
function FeaturedWork({ projects, onPlay }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  useWheelPassthrough(scrollerRef, [projects.length]);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [projects]);

  const slide = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.clientWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!projects.length) return null;
  return (
    <section className="py-20 md:py-28 bg-[#050505]" data-testid="featured-work">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="inline-block font-mono text-[11px] uppercase tracking-[0.32em] text-[#E63946] font-semibold">
              Selected Projects
            </span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tighter leading-[0.95]">
              Featured <span className="italic font-extrabold text-zinc-400">Work</span>
            </h2>
            <div className="mt-4 w-16 h-[3px] bg-[#E63946]" />
          </div>

          {/* Slider controls (desktop) */}
          <div className="hidden md:flex items-center gap-2" data-testid="featured-work-controls">
            <button
              onClick={() => slide(-1)}
              disabled={!canPrev}
              aria-label="Previous projects"
              data-testid="featured-work-prev"
              className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                canPrev
                  ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                  : "border-white/5 text-zinc-700 cursor-not-allowed"
              }`}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => slide(1)}
              disabled={!canNext}
              aria-label="Next projects"
              data-testid="featured-work-next"
              className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                canNext
                  ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                  : "border-white/5 text-zinc-700 cursor-not-allowed"
              }`}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Horizontal slider: 4 cards visible on lg, snap & scroll */}
        <div className="relative mt-10">
          <div
            ref={scrollerRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            data-testid="featured-work-scroller"
          >
            {projects.map((p, i) => (
              <motion.div
                key={p.id}
                data-card
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="group cursor-pointer flex-shrink-0 snap-start w-[70%] sm:w-[45%] md:w-[31%] lg:w-[calc((100%-48px)/4)]"
                onClick={() => onPlay(p)}
                data-testid={`featured-card-${i}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl bg-[#0F0F11] border border-white/5 group-hover:border-[#E63946]/40 transition-all duration-500">
                  {(() => { const thumb = getThumb(p); return thumb ? (
                    <img
                      src={thumb}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                      onError={(e) => { const t = e.currentTarget; if (t.src.includes("maxresdefault")) t.src = t.src.replace("maxresdefault", "hqdefault"); }}
                    />
                  ) : (
                    <iframe src={toEmbedUrl(p.embedUrl)} className="w-full h-full border-0 pointer-events-none" title={p.title} loading="lazy" scrolling="no" />
                  ); })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Play */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-[#E63946] flex items-center justify-center shadow-lg shadow-[#E63946]/50">
                      <Play size={20} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>

                {/* Info below card — clear hierarchy & high readability */}
                <div className="mt-4 px-0.5">
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-[#E63946] font-bold">
                    {p.category}
                  </span>
                  <h3 className="mt-2 font-heading text-base md:text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-[#E63946] transition-colors duration-300">
                    {p.title}
                  </h3>
                  {p.description && (
                    <p className="mt-2 text-[13px] text-zinc-300/90 leading-relaxed">
                      {p.description}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile arrows below */}
          <div className="md:hidden mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => slide(-1)}
              disabled={!canPrev}
              aria-label="Previous projects"
              data-testid="featured-work-prev-mobile"
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canPrev ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => slide(1)}
              disabled={!canNext}
              aria-label="Next projects"
              data-testid="featured-work-next-mobile"
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canNext ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── 3. BRAND COLLABORATIONS ────────────────────────────
function BrandCollabs() {
  const [brands, setBrands] = useState([]);
  useEffect(() => { axios.get(`${API}/content/brands`).then(r => setBrands(r.data.items || [])).catch(() => {}); }, []);
  if (!brands.length) return null;

  // Split into 3 rows
  const rows = [[], [], []];
  brands.forEach((b, i) => { rows[i % 3].push(b); });
  const pad = (r) => (r.length < 4 ? [...r, ...r, ...r, ...r] : [...r, ...r]);

  const BrandLogo = ({ brand }) => {
    // Fixed-width slot keeps all logos visually balanced regardless of original aspect ratio
    const inner = (
      <div className="group relative flex items-center justify-center w-[110px] sm:w-[150px] md:w-[180px] h-10 sm:h-14 md:h-16 mx-2 sm:mx-3 md:mx-4 shrink-0">
        <div className="absolute inset-0 rounded-xl bg-white/0 group-hover:bg-white/[0.06] transition-all duration-500" />
        {brand.logo ? (
          <img
            src={brand.logo}
            alt={brand.name}
            className="max-h-full max-w-full object-contain relative z-10 opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            loading="lazy"
          />
        ) : (
          <span className="font-heading text-xs sm:text-base md:text-lg font-bold text-zinc-400 group-hover:text-white whitespace-nowrap relative z-10 transition-all duration-500">
            {brand.name}
          </span>
        )}
      </div>
    );
    return brand.link ? (
      <a href={brand.link} target="_blank" rel="noopener noreferrer" data-testid={`brand-link-${brand.id}`}>{inner}</a>
    ) : inner;
  };

  return (
    <section className="py-10 md:py-20 bg-[#070708] border-y border-white/[0.03] overflow-hidden" data-testid="brand-collabs">
      <div className="text-center mb-8 md:mb-10">
        <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500">Brands I've Worked With</span>
        <div className="mt-3 mx-auto w-12 h-[2px] bg-[#E63946]/40" />
      </div>

      <div className="space-y-3 sm:space-y-5 md:space-y-6">
        <Marquee speed={30} gradient={false} pauseOnHover direction="left">
          {pad(rows[0]).map((b, i) => (<BrandLogo key={`pr1-${b.id}-${i}`} brand={b} />))}
        </Marquee>

        {rows[1].length > 0 && (
          <Marquee speed={30} gradient={false} pauseOnHover direction="right">
            {pad(rows[1]).map((b, i) => (<BrandLogo key={`pr2-${b.id}-${i}`} brand={b} />))}
          </Marquee>
        )}

        {rows[2].length > 0 && (
          <Marquee speed={30} gradient={false} pauseOnHover direction="left">
            {pad(rows[2]).map((b, i) => (<BrandLogo key={`pr3-${b.id}-${i}`} brand={b} />))}
          </Marquee>
        )}
      </div>

      {/* See All button */}
      <div className="mt-8 md:mt-12 flex justify-center">
        <Link
          to="/brands"
          data-testid="brands-see-all-portfolio"
          className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-xs sm:text-sm font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-[#E63946] hover:bg-[#E63946]/5 transition-all duration-300"
        >
          See All Brands
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </div>
    </section>
  );
}

// ── 4. CATEGORY GALLERY ────────────────────────────────
function CategoryGallery({ categories, gallery, onPlay }) {
  const [active, setActive] = useState("All Projects");
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  useWheelPassthrough(scrollerRef, [active]);

  const isOverview = active === "All Projects";
  const filtered = isOverview ? [] : gallery.filter((g) => g.category === active);
  const activeCat = categories.find((c) => c.name === active);
  const userCategories = categories.filter((c) => c.name !== "All Projects");

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    if (isOverview) return;
    // Reset scroll & re-measure when switching categories
    const el = scrollerRef.current;
    if (el) el.scrollLeft = 0;
    const t = setTimeout(updateArrows, 50);
    const onResize = () => updateArrows();
    const onScroll = () => updateArrows();
    el?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      el?.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [active, filtered.length, isOverview]);

  const slide = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="py-20 md:py-28 bg-[#050505]" data-testid="category-gallery">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Explore</span>
            <div className="mt-2 flex items-center gap-4 md:gap-6 flex-wrap">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                {isOverview ? "All Projects" : active}
              </h2>
              <AnimatePresence mode="wait">
                {activeCat?.logo && (
                  <motion.div
                    key={activeCat.id}
                    initial={{ opacity: 0, scale: 0.6, rotate: -8, filter: "blur(8px)" }}
                    animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 0.6, rotate: 8, filter: "blur(8px)" }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-2"
                    data-testid="active-category-logo"
                  >
                    <img src={activeCat.logo} alt={activeCat.name} className="w-full h-full object-contain" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Slider controls — only when a category is selected */}
          {!isOverview && filtered.length > 0 && (
            <div className="hidden md:flex items-center gap-2" data-testid="gallery-controls">
              <button
                onClick={() => slide(-1)}
                disabled={!canPrev}
                aria-label="Previous projects"
                data-testid="gallery-prev"
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canPrev
                    ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                    : "border-white/5 text-zinc-700 cursor-not-allowed"
                }`}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => slide(1)}
                disabled={!canNext}
                aria-label="Next projects"
                data-testid="gallery-next"
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                  canNext
                    ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                    : "border-white/5 text-zinc-700 cursor-not-allowed"
                }`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Category filters — only visible when a category is selected */}
        <AnimatePresence>
          {!isOverview && (
            <motion.div
              key="filter-pills"
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-8 flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActive(cat.name)}
                    data-testid={`category-tab-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2 text-xs md:text-sm font-medium tracking-wide transition-all duration-300 rounded-full ${
                      active === cat.name
                        ? "bg-[#E63946] text-white shadow-lg shadow-[#E63946]/30"
                        : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {!isOverview && activeCat?.description && (
            <motion.p
              key={activeCat.id + "-desc"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.35 }}
              className="mt-4 text-sm text-zinc-400 max-w-2xl leading-relaxed"
            >
              {activeCat.description}
            </motion.p>
          )}
        </AnimatePresence>

        {/* OVERVIEW MODE — compact category tiles, no video content, no pill list */}
        <AnimatePresence mode="wait">
          {isOverview ? (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2 md:gap-3"
              data-testid="category-overview"
            >
              {userCategories.map((cat, i) => {
                const count = gallery.filter((g) => g.category === cat.name).length;
                return (
                  <motion.button
                    key={cat.id}
                    onClick={() => setActive(cat.name)}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.4 }}
                    whileHover={{ y: -3 }}
                    className="group relative aspect-square rounded-xl bg-gradient-to-br from-[#0F0F11] to-[#080809] border border-white/5 hover:border-[#E63946]/40 transition-all duration-500 overflow-hidden text-left"
                    data-testid={`category-tile-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {/* Red corner glow */}
                    <div className="absolute -top-8 -right-8 w-20 h-20 bg-[#E63946]/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                    <div className="relative h-full flex flex-col items-center justify-center p-2 md:p-3 text-center">
                      {/* Logo */}
                      {cat.logo ? (
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center p-1.5 mb-2 group-hover:scale-110 group-hover:border-[#E63946]/40 transition-all duration-500">
                          <img src={cat.logo} alt={cat.name} className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-500">
                          <Play size={14} className="text-[#E63946]" />
                        </div>
                      )}
                      <h3 className="font-heading text-[11px] md:text-xs font-bold text-white tracking-tight leading-tight line-clamp-2">
                        {cat.name}
                      </h3>
                      <span className="mt-1 font-mono text-[8px] md:text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                        {count} {count === 1 ? "proj" : "projects"}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          ) : (
            /* CATEGORY MODE — horizontal video slider */
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative mt-10"
            >
              {filtered.length === 0 ? (
                <p className="mt-12 text-center text-zinc-600 text-sm">No projects in this category yet.</p>
              ) : (
                <div
                  ref={scrollerRef}
                  className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  data-testid="gallery-scroller"
                >
                  {filtered.map((item, i) => (
                    <motion.div
                      key={item.id}
                      data-gallery-card
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06, duration: 0.5 }}
                      className="group cursor-pointer flex-shrink-0 snap-start w-[78%] sm:w-[48%] md:w-[40%] lg:w-[calc((100%-48px)/3)]"
                      onClick={() => onPlay(item)}
                      data-testid={`gallery-card-${i}`}
                    >
                      <div className="relative aspect-video overflow-hidden rounded-xl bg-[#0F0F11] border border-white/5 group-hover:border-[#E63946]/40 transition-all duration-500">
                        {(() => {
                          const thumb = getThumb(item);
                          return thumb ? (
                            <img
                              src={thumb}
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              loading="lazy"
                              onError={(e) => {
                                const t = e.currentTarget;
                                if (t.src.includes("maxresdefault"))
                                  t.src = t.src.replace("maxresdefault", "hqdefault");
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#1A1A1E] to-[#0F0F11] flex items-center justify-center">
                              <Play size={24} className="text-zinc-700" />
                            </div>
                          );
                        })()}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-14 h-14 rounded-full bg-[#E63946] flex items-center justify-center shadow-xl shadow-[#E63946]/50">
                            <Play size={20} className="text-white ml-0.5" fill="white" />
                          </div>
                        </div>
                        {item.videos && item.videos.trim() && (
                          <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur border border-white/10 rounded-full">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white font-semibold">
                              +{item.videos.split(/[\n,]/).filter((s) => s.trim()).length} videos
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 px-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#E63946] font-bold">{item.category}</span>
                          {item.year && <span className="font-mono text-[10px] text-zinc-600">· {item.year}</span>}
                        </div>
                        <h4 className="mt-2 font-heading text-base md:text-lg font-bold text-white leading-snug group-hover:text-[#E63946] transition-colors duration-300">
                          {item.title}
                        </h4>
                        {item.description && (
                          <p className="mt-2 text-sm text-zinc-300/90 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Mobile arrows below */}
              {filtered.length > 0 && (
                <div className="md:hidden mt-6 flex items-center justify-center gap-3">
                  <button
                    onClick={() => slide(-1)}
                    disabled={!canPrev}
                    aria-label="Previous projects"
                    data-testid="gallery-prev-mobile"
                    className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                      canPrev ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
                    }`}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => slide(1)}
                    disabled={!canNext}
                    aria-label="Next projects"
                    data-testid="gallery-next-mobile"
                    className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                      canNext ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
                    }`}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ── 5a. TELEVISION APPEARANCE (Highlighted Feature) ────
function TelevisionAppearance() {
  return (
    <section className="py-20 md:py-28 bg-[#050505] relative overflow-hidden" data-testid="television-appearance">
      {/* Cinematic glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[55%] bg-[#E63946]/[0.08] blur-[140px] rounded-full" />
      </div>
      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E63946]/10 border border-[#E63946]/40 flex items-center justify-center">
            <Tv size={18} className="text-[#E63946]" />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#E63946] font-bold">On Air</span>
        </div>
        <h2 className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tighter leading-[0.95]">
          Television <span className="italic font-extrabold text-zinc-400">Appearance</span>
        </h2>
        <p className="mt-5 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Featured on television discussing content creation, technology, and the creator economy.
        </p>

        {/* Cinematic video card */}
        <a
          href="https://youtu.be/G8jMTQJVs6s"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tv-video-card"
          className="mt-12 block relative group"
        >
          <div className="absolute -inset-[2px] bg-gradient-to-r from-[#E63946]/50 via-[#E63946]/10 to-[#E63946]/50 rounded-2xl blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0C] shadow-[0_30px_80px_-20px_rgba(230,57,70,0.25)]">
            {/* Poster */}
            <div className="relative aspect-video bg-black overflow-hidden">
              <img
                src="https://img.youtube.com/vi/G8jMTQJVs6s/maxresdefault.jpg"
                alt="Featured Interview on Al Mashad TV"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                loading="lazy"
                onError={(e) => { e.currentTarget.src = "https://img.youtube.com/vi/G8jMTQJVs6s/hqdefault.jpg"; }}
              />
              {/* Cinematic gradients */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

              {/* Live tag */}
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-full">
                <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white font-semibold">Al Mashad TV</span>
              </div>

              {/* Big play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-[#E63946]/30 blur-2xl scale-150 group-hover:scale-[2] transition-transform duration-700" />
                  <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#E63946] flex items-center justify-center shadow-2xl shadow-[#E63946]/50 group-hover:scale-110 transition-transform duration-500">
                    <Play size={32} className="text-white ml-1" fill="white" />
                  </div>
                </div>
              </div>

              {/* Bottom title overlay on poster */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#E63946] font-bold">Featured Interview</span>
                <h3 className="mt-2 font-heading text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight drop-shadow-2xl">
                  Featured Interview on Al Mashad TV
                </h3>
              </div>
            </div>

            {/* Description footer */}
            <div className="p-6 md:p-8 bg-gradient-to-b from-[#0A0A0C] to-[#050505] border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-3xl">
                George Emad was featured in a television interview discussing content creation, technology, and his journey as a creator.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#E63946] group-hover:text-white transition-colors whitespace-nowrap">
                Watch on YouTube <ExternalLink size={14} />
              </span>
            </div>
          </div>
        </a>
      </div>
    </section>
  );
}

// ── 5. PRESS FEATURES (horizontal slider) ──────────────
function MediaPress({ items, onPlay }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  useWheelPassthrough(scrollerRef, [items?.length || 0]);

  // Hide the dedicated TV interview from this section
  const filtered = (items || []).filter((it) => {
    const c = (it.category || "").toLowerCase();
    const t = (it.title || "").toLowerCase();
    return !c.includes("television") && !t.includes("television interview");
  });

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [filtered.length]);

  const slide = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-press-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!filtered.length) return null;
  return (
    <section className="py-20 md:py-28 bg-[#0A0A0C]" data-testid="press-features">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Press</span>
            <h2 className="mt-2 font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">Press Features</h2>
            <p className="mt-2 text-sm text-zinc-500 max-w-xl">Podcasts, guest appearances, and other media features.</p>
          </div>

          {/* Slider controls (desktop) */}
          <div className="hidden md:flex items-center gap-2" data-testid="press-controls">
            <button
              onClick={() => slide(-1)}
              disabled={!canPrev}
              aria-label="Previous press features"
              data-testid="press-prev"
              className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                canPrev
                  ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                  : "border-white/5 text-zinc-700 cursor-not-allowed"
              }`}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => slide(1)}
              disabled={!canNext}
              aria-label="Next press features"
              data-testid="press-next"
              className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all duration-300 ${
                canNext
                  ? "border-white/30 text-white hover:bg-[#E63946] hover:border-[#E63946]"
                  : "border-white/5 text-zinc-700 cursor-not-allowed"
              }`}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Horizontal slider */}
        <div className="relative mt-10">
          <div
            ref={scrollerRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            data-testid="press-scroller"
          >
            {filtered.map((item, i) => (
              <motion.div
                key={item.id}
                data-press-card
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group border border-white/5 bg-[#0F0F11] rounded-xl overflow-hidden hover:border-[#E63946]/20 transition-all duration-500 cursor-pointer flex-shrink-0 snap-start w-[78%] sm:w-[48%] md:w-[40%] lg:w-[calc((100%-48px)/3)]"
                onClick={() => item.link ? window.open(item.link, "_blank") : onPlay(item)}
                data-testid={`press-card-${i}`}
              >
                <div className="relative aspect-video overflow-hidden bg-[#0A0A0C]">
                  {(() => { const thumb = getThumb(item); return thumb ? (
                    <img
                      src={thumb}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                      onError={(e) => { const t = e.currentTarget; if (t.src.includes("maxresdefault")) t.src = t.src.replace("maxresdefault", "hqdefault"); }}
                    />
                  ) : item.embedUrl ? (
                    <iframe src={toEmbedUrl(item.embedUrl)} className="w-full h-full border-0 pointer-events-none" title={item.title} loading="lazy" scrolling="no" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Play size={24} className="text-zinc-700" /></div>
                  ); })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#E63946]">{item.category}</span>
                  <h4 className="mt-1 font-heading text-sm font-semibold text-white group-hover:text-[#E63946] transition-colors">{item.title}</h4>
                  {item.date && <p className="mt-1 text-[10px] text-zinc-600">{item.date}</p>}
                  <span className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-500 group-hover:text-[#E63946] transition-colors">
                    Watch <ExternalLink size={10} />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile arrows below */}
          <div className="md:hidden mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => slide(-1)}
              disabled={!canPrev}
              aria-label="Previous press features"
              data-testid="press-prev-mobile"
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canPrev ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => slide(1)}
              disabled={!canNext}
              aria-label="Next press features"
              data-testid="press-next-mobile"
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                canNext ? "border-white/30 text-white" : "border-white/5 text-zinc-700"
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── 6. TESTIMONIALS ────────────────────────────────────
function Testimonials({ items }) {
  const [current, setCurrent] = useState(0);
  if (!items.length) return null;
  const next = () => setCurrent((current + 1) % items.length);
  const prev = () => setCurrent((current - 1 + items.length) % items.length);

  return (
    <section className="py-20 md:py-28 bg-[#050505]" data-testid="testimonials">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Testimonials</span>
        <h2 className="mt-2 font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">What They Say</h2>

        <div className="mt-12 relative">
          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }} className="px-4">
              <Quote size={32} className="text-[#E63946]/30 mx-auto mb-6" />
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed italic max-w-2xl mx-auto">"{items[current].quote}"</p>
              <div className="mt-6">
                <p className="font-heading text-sm font-semibold text-white">{items[current].name}</p>
                <p className="text-xs text-zinc-500">{items[current].position}{items[current].company ? ` at ${items[current].company}` : ""}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          {items.length > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button onClick={prev} className="p-2 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition-colors rounded-full"><ChevronLeft size={18} /></button>
              <div className="flex gap-1.5">
                {items.map((_, i) => (<div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === current ? "bg-[#E63946]" : "bg-zinc-800"}`} />))}
              </div>
              <button onClick={next} className="p-2 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition-colors rounded-full"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── 7. FINAL CTA ───────────────────────────────────────
function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-[#0A0A0C] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/5 via-transparent to-[#E63946]/5" />
      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Collaborate</span>
        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-black tracking-tighter text-white">
          Let's Create Something<br /><span className="text-[#E63946]">Great Together</span>
        </h2>
        <p className="mt-6 text-sm md:text-base text-zinc-400 max-w-lg mx-auto leading-relaxed">
          Whether you're a brand, agency, production company, or creator program, I'm always open to discussing exciting projects and collaborations.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#E63946] text-white font-semibold tracking-wide hover:bg-[#F25C69] transition-colors">
            Contact Me <ArrowRight size={18} />
          </Link>
          <a href="#featured-work" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-semibold tracking-wide hover:border-white/50 hover:bg-white/5 transition-all">
            View My Work
          </a>
        </div>
      </div>
    </section>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────
export default function Portfolio() {
  const [hero, setHero] = useState({ videoUrl: "", subtitle: "", stats: [] });
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [media, setMedia] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/content/portfolio-hero`),
      axios.get(`${API}/content/featured-projects`),
      axios.get(`${API}/content/portfolio-categories`),
      axios.get(`${API}/content/gallery`),
      axios.get(`${API}/content/media-press`),
      axios.get(`${API}/content/testimonials`),
    ]).then(([h, f, c, g, m, t]) => {
      setHero(h.data);
      setFeatured(f.data.items || []);
      setCategories(c.data.items || []);
      setGallery(g.data.items || []);
      setMedia(m.data.items || []);
      setTestimonials(t.data.items || []);
    }).catch(() => {});
  }, []);

  return (
    <main data-testid="portfolio-page">
      <PortfolioHero hero={hero} />
      <div id="featured-work"><FeaturedWork projects={featured} onPlay={setSelectedVideo} /></div>
      <BrandCollabs />
      {categories.length > 0 && <CategoryGallery categories={categories} gallery={gallery} onPlay={setSelectedVideo} />}
      <TelevisionAppearance />
      <MediaPress items={media} onPlay={setSelectedVideo} />
      <Testimonials items={testimonials} />
      <FinalCTA />

      <AnimatePresence>
        {selectedVideo && <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} isVertical={selectedVideo.embedUrl?.includes("instagram") || selectedVideo.videoUrl?.includes("shorts")} />}
      </AnimatePresence>
    </main>
  );
}
